import { Box, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip, Chip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useDispatch, useSelector } from "react-redux";
import { updateStallForm, updateStallModal, updateStallEdit, updateSelectedStallId, updateStallLoading, updateStalls } from "../Redux/inventory";
import { useEffect, useState } from "react";
import CustomAxios from "../utils/CustomAxios";
import useStatusToggle from "../hooks/useStatusToggle";
import CircularProgress from '@mui/material/CircularProgress';
import { confirmDialog } from 'primereact/confirmdialog';

export function StallTable() {
    const dispatch = useDispatch();
    const stalls = useSelector(state => state.inventory.stalls);
    const stallLoading = useSelector(state => state.inventory.stallLoading);
    const [stallData, setStallData] = useState([]);
    const statusToggle = useStatusToggle();
    const user_role = localStorage.getItem('role');
    
    const assignedHubs = JSON.parse(localStorage.getItem('hub') || '[]');
    const currentUserId = parseInt(localStorage.getItem('id'));

    const isUserAssignedToStallHub = (stall) => {
        if (user_role === 'super_admin') return true; 
        
        const stallHubId = stall.hub || stall.hub_id;
        return assignedHubs.includes(stallHubId);
    };

    useEffect(() => {
        fetchStalls();
    }, []);

    useEffect(() => {
        if (stalls?.length > 0) {
            setStallData(stalls);
        }
    }, [stalls]);

    const fetchStalls = async () => {
        try {
            dispatch(updateStallLoading(true));
            const response = await CustomAxios.get('stalls/?include_inactive=true');
            dispatch(updateStalls(response.data));
        } catch (error) {
            console.error('Error fetching stalls:', error);
        } finally {
            dispatch(updateStallLoading(false));
        }
    };

    const handleEditStall = (stall) => {
        dispatch(updateStallForm({
            hub: stall.hub,
            stall_name: stall.stall_name,
            pincode: stall.pincode,
            latitude: stall.latitude,
            longitude: stall.longitude,
            stall_address: stall.stall_address,
            icon_url: stall.icon_url || '',
            is_active: stall.is_active,
            isViewOnly: false
        }));
        dispatch(updateSelectedStallId(stall.id));
        dispatch(updateStallEdit(true));
        dispatch(updateStallModal(true));
    };

    const confirmDeleteStall = (stall) => {
        confirmDialog({
            message: (
                <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
                        <Box>
                            <Typography>Stall Name: {stall.stall_name}</Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>Do you want to {stall.is_active == true ? 'Deactivate' : 'Activate'} this Stall</Typography>
                </Box>
            ),
            header: 'Confirmation',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            // Guarded on accept rather than on the icon: the dialog can be
            // accepted twice as easily as the icon can be clicked twice.
            accept: async () => {
                const next = !stall.is_active;
                await statusToggle.run(stall.id, next, async () => {
                    try {
                        await CustomAxios.patch(`stalls/update/${stall.id}/`, { is_active: next });
                    } catch (error) {
                        console.error('Error updating stall:', error);
                        // Keep current UI unchanged on failure and re-sync from server
                        fetchStalls();
                        throw error;
                    }
                    const updatedStall = { ...stall, is_active: next };
                    setStallData((prev) => prev.map((s) => s.id === stall.id ? updatedStall : s));
                    dispatch(updateStalls(stalls.map((s) => s.id === stall.id ? updatedStall : s)));
                });
            },
        });
    };

    const handleViewStall = (stall) => {
        dispatch(updateStallForm({
            hub: stall.hub,
            stall_name: stall.stall_name,
            pincode: stall.pincode,
            latitude: stall.latitude,
            longitude: stall.longitude,
            stall_address: stall.stall_address,
            icon_url: stall.icon_url || '',
            is_active: stall.is_active,
            isViewOnly: true
        }));
        dispatch(updateSelectedStallId(stall.id));
        dispatch(updateStallEdit(false));
        dispatch(updateStallModal(true));
    };

    if (stallLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ backgroundColor: "white", borderRadius: "8px", padding: "24px", marginTop: "20px" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px", marginBottom: "20px" }}>
                Stalls Management
            </Typography>
            
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
                            <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>Stall Name</TableCell>
                            <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>Hub</TableCell>
                            <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>Pincode</TableCell>
                            <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>Address</TableCell>
                            <TableCell sx={{ fontWeight: "600", textAlign: "left" }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: "600", textAlign: "center" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stallData.length > 0 ? (
                            stallData.map((stall) => (
                                <TableRow key={stall.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                    <TableCell sx={{ textAlign: "left", textTransform: "capitalize" }}>
                                        {stall.stall_name}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: "left", textTransform: "capitalize" }}>
                                        {stall.hub_name || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: "left" }}>
                                        {stall.pincode}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: "left", maxWidth: "200px" }}>
                                        <Typography sx={{ 
                                            overflow: "hidden", 
                                            textOverflow: "ellipsis", 
                                            whiteSpace: "nowrap",
                                            fontSize: "14px"
                                        }}>
                                            {stall.stall_address}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: "left" }}>
                                        {stall.is_active == false ? (
                                            <Box sx={{ width: "120px", height: "22px", backgroundColor: "button.backgroundDelete", border: "1px solid", borderColor: "borders.red", borderRadius: "2px", padding: "1px 8px", display: "flex", justifyContent: "center" }}>
                                                <Typography sx={{ fontSize: "12px", fontWeight: 400, lineHeight: "20px", color: "borders.red", textAlign: "center", textTransform: "uppercase", width: "88px" }}> Inactive</Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ width: "67px", height: "22px", backgroundColor: "button.backgroundEdit", border: "1px solid", borderColor: "borders.green", borderRadius: "2px", padding: "1px 8px", display: "flex", justifyContent: "center" }} >
                                                <Typography sx={{ textAlign: "center", fontSize: "12px", fontWeight: 400, lineHeight: "20px", color: "borders.green", textTransform: "uppercase", width: "67px" }}>Active</Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: "center" }}>
                                        <Box sx={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                            {(() => {
                                                if (user_role === 'super_admin') {
                                                    // Super Admin: Can view, edit, and activate/deactivate all stalls
                                                    return (
                                                        <>
                                                            <Tooltip title="View stall details" arrow>
                                                                <IconButton 
                                                                    sx={{ textTransform: "none" }} 
                                                                    onClick={() => handleViewStall(stall)}
                                                                >
                                                                    <VisibilityIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Edit stall" arrow>
                                                                <IconButton 
                                                                    sx={{ textTransform: "none" }} 
                                                                    onClick={() => handleEditStall(stall)}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={stall.is_active == true ? "Deactivate" : "Activate"} arrow>
                                                                <IconButton sx={{ textTransform: "none" }} disabled={statusToggle.isBusy} onClick={() => confirmDeleteStall(stall)}>
                                                                    {statusToggle.isPending(stall.id)
                                                                        ? <CircularProgress size={18} />
                                                                        : (stall.is_active == true ? <DeleteOutlineIcon /> : <TaskAltIcon />)}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    );
                                                } else if (user_role === 'admin') {
                                                    // Admin: Can view all stalls, but only edit/activate/deactivate assigned hub stalls
                                                    const isAssignedToStallHub = isUserAssignedToStallHub(stall);
                                                    
                                                    return (
                                                        <>
                                                            <Tooltip title="View stall details" arrow>
                                                                <IconButton 
                                                                    sx={{ textTransform: "none" }} 
                                                                    onClick={() => handleViewStall(stall)}
                                                                >
                                                                    <VisibilityIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {isAssignedToStallHub ? (
                                                                <>
                                                                    <Tooltip title="Edit stall" arrow>
                                                                        <IconButton 
                                                                            sx={{ textTransform: "none" }} 
                                                                            onClick={() => handleEditStall(stall)}
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title={stall.is_active == true ? "Deactivate" : "Activate"} arrow>
                                                                        <IconButton sx={{ textTransform: "none" }} disabled={statusToggle.isBusy} onClick={() => confirmDeleteStall(stall)}>
                                                                            {statusToggle.isPending(stall.id)
                                                                                ? <CircularProgress size={18} />
                                                                                : (stall.is_active == true ? <DeleteOutlineIcon /> : <TaskAltIcon />)}
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Tooltip title="View Only - Not assigned to this hub" arrow>
                                                                        <IconButton 
                                                                            sx={{ textTransform: "none", opacity: 0.5 }} 
                                                                            disabled
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="View Only - Not assigned to this hub" arrow>
                                                                        <IconButton 
                                                                            sx={{ textTransform: "none", opacity: 0.5 }} 
                                                                            disabled
                                                                        >
                                                                            <DeleteOutlineIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                        </>
                                                    );
                                                } else if (user_role === 'supervisor') {
                                                    // Supervisor: Can view all stalls, but only edit assigned stalls (limited fields)
                                                    const isAssignedToStallHub = isUserAssignedToStallHub(stall);
                                                    
                                                    return (
                                                        <>
                                                            <Tooltip title="View stall details" arrow>
                                                                <IconButton 
                                                                    sx={{ textTransform: "none" }} 
                                                                    onClick={() => handleViewStall(stall)}
                                                                >
                                                                    <VisibilityIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {isAssignedToStallHub ? (
                                                                <Tooltip title="Edit stall (Limited)" arrow>
                                                                    <IconButton 
                                                                        sx={{ textTransform: "none" }} 
                                                                        onClick={() => handleEditStall(stall)}
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip title="View Only - Not assigned to this hub" arrow>
                                                                    <IconButton 
                                                                        sx={{ textTransform: "none", opacity: 0.5 }} 
                                                                        disabled
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    );
                                                } else {
                                                    // Other roles: View only
                                                    return (
                                                        <Tooltip title="View Only" arrow>
                                                            <IconButton 
                                                                sx={{ textTransform: "none" }} 
                                                                onClick={() => handleViewStall(stall)}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    );
                                                }
                                            })()}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell sx={{ textAlign: "center", textTransform: "capitalize", backgroundColor: "white", height: "200px", fontWeight: "700" }} colSpan={6}>
                                    No Stalls Available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
